import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { FabricChannel } from '@atlaskit/analytics-listeners';
import {
	useAnalyticsEvents,
	AnalyticsListener,
	type UIAnalyticsEvent,
} from '@atlaskit/analytics-next';
import MentionIcon from '@atlaskit/icon/glyph/mention';
import Avatar from '@atlaskit/avatar/Avatar';

import { useSessionData } from '@confluence/session-data';
import { useShareToEntities } from '@confluence/share';
import type { FlagsStateContainer } from '@confluence/flags';

import { useContentPermissions } from './hooks/useContentPermissions';
import { useMentionsUserInfoQuery } from './hooks/useMentionsUserInfoQuery';
import { useCreateMentionNotificationMutation } from './hooks/useCreateMentionNotificationMutation';
import { useCreateInlineTaskNotificationMutation } from './hooks/useCreateInlineTaskNotificationMutation';
import { ContentRole, PrincipalType } from './graphql/__types__/UpdateContentPermissionsMutation';
import { Operation } from './graphql/__types__/CreateInlineTaskNotificationMutation';

type MentionsNotificationsWrapperProps = {
	children: React.ReactNode;
	flags: FlagsStateContainer;
	contentId: string;
	isMentionsNotificationsEnabled: boolean;
};

type ShowCustomFlagProps = {
	title: string;
	description: string;
	userPhoto?: string;
};

type ShowCustomFlagWithActionProps = {
	title: string;
	description: string;
	userPhoto?: string;
	actionContent: string;
	actionOnClick: () => void;
	handleOnClose?: () => void;
};

type ShowFlagForUserProps = {
	displayName: string;
	userPhoto?: string;
};

const getElapsedTime = (startTime: React.MutableRefObject<number | null>) => {
	const endTime = Date.now();
	let elapsedTimeInSeconds = 0;
	if (startTime.current !== null) {
		elapsedTimeInSeconds = (endTime - startTime.current) / 1000; // Convert to seconds
	}
	return elapsedTimeInSeconds;
};

export const MentionsNotificationsWrapper = ({
	children,
	flags,
	contentId,
	isMentionsNotificationsEnabled,
}: MentionsNotificationsWrapperProps) => {
	const [mentionedUserId, setMentionedUserId] = useState<string>('');
	const [mentionLocalId, setMentionLocalId] = useState<string>('');
	const [taskItem, setTaskItem] = useState({
		operation: '',
		recipientAccountId: '',
		taskId: '',
	});
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { userId: currentUserId } = useSessionData();
	const { shareToEntities } = useShareToEntities(contentId || '');

	// Inline comment sidebar uses the same editor component as the one that powers the page itself
	const isSidebarPresent =
		document.querySelectorAll('.ak-editor-content-area > .ProseMirror').length > 1;

	const resetState = () => {
		setMentionedUserId('');
		setMentionLocalId('');
		setTaskItem({
			operation: '',
			recipientAccountId: '',
			taskId: '',
		});
	};

	// Hooks for fetching user display name, content permissions, and sending notifications
	const { userInfoData, userInfoError } = useMentionsUserInfoQuery({
		mentionedUserId,
		currentUserId,
		isMentionsNotificationsEnabled,
	});

	const showErrorFlag = useCallback(
		(displayName: string) => {
			void flags.showErrorFlag({
				title: formatMessage(messages.mentionFlagFailedTitle, {
					displayName,
				}),
				description: formatMessage(messages.mentionFlagFailedDescription),
				isAutoDismiss: true,
			});
		},
		[flags, formatMessage],
	);

	const {
		userHasAccessToContent,
		isParentPageRestricted,
		isSpaceRestricted,
		contentPermissionsError,
		contentPermissionsLoading,
		updateContentPermissionMutation,
	} = useContentPermissions({
		contentId,
		mentionedUserId,
		currentUserId,
		isMentionsNotificationsEnabled,
	});

	const { sendCreateMentionNotification } = useCreateMentionNotificationMutation({
		contentId,
		showErrorFlag,
	});

	const { sendCreateInlineTaskNotification } = useCreateInlineTaskNotificationMutation({
		contentId,
		showErrorFlag,
	});

	const startTime = useRef<number | null>(null);

	// `showCustomFlag` for confirmation flags without CTA
	const showCustomFlag = useCallback(
		({ title, description, userPhoto }: ShowCustomFlagProps) => {
			void flags.showCustomFlag({
				title,
				description,
				isAutoDismiss: true,
				customIcon: userPhoto ? (
					<Avatar src={userPhoto} label="mention" />
				) : (
					<MentionIcon label="mention" />
				),
			});
		},
		[flags],
	);

	// `showCustomFlagWithAction` for unrestricted users with "do not notify" CTA
	// & share now for restricted users
	const showCustomFlagWithAction = useCallback(
		({
			title,
			description,
			userPhoto,
			actionContent,
			actionOnClick,
			handleOnClose,
		}: ShowCustomFlagWithActionProps) => {
			void flags.showCustomFlag({
				title,
				description,
				isAutoDismiss: true,
				customIcon: userPhoto ? (
					<Avatar src={userPhoto} label="mention" />
				) : (
					<MentionIcon label="mention" />
				),
				onClose: handleOnClose && handleOnClose,
				actions: [
					{
						content: actionContent,
						onClick: actionOnClick,
					},
				],
			});
			startTime.current = Date.now();
		},
		[flags],
	);

	const showFlagForRestrictedUser = useCallback(
		({ displayName, userPhoto }: ShowFlagForUserProps) => {
			const isTaskMention = taskItem.recipientAccountId;

			// Page can have restrictions or restrictions inherited from parent
			if (isSpaceRestricted) {
				showCustomFlag({
					title: isTaskMention
						? formatMessage(messages.taskMentionFlagNoPermissionsTitle, {
								displayName,
							})
						: formatMessage(messages.mentionFlagNoPermissionsTitle, {
								displayName,
							}),
					description: isTaskMention
						? formatMessage(messages.taskMentionFlagNoPermissionsSpaceRestrictionsDescription)
						: formatMessage(messages.mentionFlagNoPermissionsSpaceRestrictionsDescription),
					userPhoto,
				});
			} else if (isParentPageRestricted) {
				showCustomFlag({
					title: isTaskMention
						? formatMessage(messages.taskMentionFlagNoPermissionsTitle, {
								displayName,
							})
						: formatMessage(messages.mentionFlagNoPermissionsTitle, {
								displayName,
							}),
					description: isTaskMention
						? formatMessage(messages.taskMentionFlagNoPermissionsParentRestrictionsDescription)
						: formatMessage(messages.mentionFlagNoPermissionsParentRestrictionsDescription),
					userPhoto,
				});
			} else {
				// Mentioned user has no access to page
				showCustomFlagWithAction({
					title: isTaskMention
						? formatMessage(messages.taskMentionFlagNoPermissionsTitle, {
								displayName,
							})
						: formatMessage(messages.mentionFlagNoPermissionsTitle, {
								displayName,
							}),
					description: isTaskMention
						? formatMessage(messages.taskMentionFlagNoPermissionsDescription)
						: formatMessage(messages.mentionFlagNoPermissionsDescription),
					userPhoto,
					actionContent: formatMessage(messages.mentionFlagShareNowButton),
					actionOnClick: async () => {
						resetState();

						// Updates page restrictions
						void updateContentPermissionMutation({
							variables: {
								contentId,
								input: {
									contentRole: ContentRole.EDITOR,
									principalId: mentionedUserId,
									principalType: PrincipalType.USER,
								},
							},
						});

						const flagToDismiss = flags.state.flags[0]?.id;
						void flags.hideFlag(flagToDismiss);

						try {
							// Sends email notifications
							const response = await shareToEntities([
								{
									id: mentionedUserId,
									type: 'user',
								},
							]);

							if (response.success) {
								createAnalyticsEvent({
									type: 'sendTrackEvent',
									data: {
										source: 'mentionsNotifications',
										action: 'sent',
										actionSubject: 'shareNotification',
									},
								}).fire();
							}
						} catch (error) {
							createAnalyticsEvent({
								type: 'sendTrackEvent',
								data: {
									source: 'mentionsNotifications',
									action: 'failed',
									actionSubject: 'shareNotification',
								},
							}).fire();

							showCustomFlag({
								title: formatMessage(messages.mentionFlagShareFailedTitle),
								description: formatMessage(messages.mentionFlagShareFailedDescription),
							});
						}
					},
				});
			}
		},
		// `shareToEntities` is a hook responsible for sending email notifications to the mentioned user.
		// When included in the dependency array, for some reason, the flag infinitely re-renders.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			contentId,
			createAnalyticsEvent,
			flags,
			formatMessage,
			isParentPageRestricted,
			isSpaceRestricted,
			mentionedUserId,
			showCustomFlag,
			showCustomFlagWithAction,
			updateContentPermissionMutation,
		],
	);

	const showFlagForUnrestrictedUser = useCallback(
		({ displayName, userPhoto }: ShowFlagForUserProps) => {
			const isTaskMention = taskItem.recipientAccountId;

			showCustomFlagWithAction({
				title: isTaskMention
					? formatMessage(messages.taskMentionFlagTitle, { displayName })
					: formatMessage(messages.mentionFlagTitle, { displayName }),
				description: isTaskMention
					? formatMessage(messages.taskMentionFlagDescription)
					: formatMessage(messages.mentionFlagDescription),
				userPhoto,
				actionContent: formatMessage(messages.mentionFlagDoNotNotifyButton),
				handleOnClose: () => {
					isTaskMention
						? sendCreateInlineTaskNotification({
								taskItem,
								displayName,
							})
						: sendCreateMentionNotification({
								mentionedUserId,
								displayName,
								mentionLocalId,
							});

					const elapsedTimeInSeconds = getElapsedTime(startTime);

					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							source: 'mentionsNotifications',
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'dismiss',
							attributes: isTaskMention
								? {
										contentId,
										operation: taskItem.operation,
										mentionedUserId: taskItem.recipientAccountId,
										taskId: taskItem.taskId,
										elapsedTimeInSeconds,
									}
								: {
										contentId,
										mentionedUserId,
										mentionLocalId,
										elapsedTimeInSeconds,
									},
						},
					}).fire();

					resetState();
				},
				actionOnClick: () => {
					resetState();

					const elapsedTimeInSeconds = getElapsedTime(startTime);

					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							source: 'mentionsNotifications',
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: isTaskMention ? 'doNotAssign' : 'doNotNotify',
							attributes: isTaskMention
								? {
										contentId,
										operation: taskItem.operation,
										mentionedUserId: taskItem.recipientAccountId,
										taskId: taskItem.taskId,
										elapsedTimeInSeconds,
									}
								: {
										contentId,
										mentionedUserId,
										mentionLocalId,
										elapsedTimeInSeconds,
									},
						},
					}).fire();

					// Only one flag shows at a given time, so we can just always target the "top" flag
					const flagToDismiss = flags.state.flags[0]?.id;
					void flags.hideFlag(flagToDismiss);
				},
			});
		},
		[
			contentId,
			createAnalyticsEvent,
			flags,
			formatMessage,
			mentionedUserId,
			showCustomFlagWithAction,
			sendCreateMentionNotification,
			sendCreateInlineTaskNotification,
			taskItem,
			mentionLocalId,
		],
	);

	// Hook for showing error flag without display name
	useEffect(() => {
		if (!isMentionsNotificationsEnabled) {
			return;
		}

		if (userInfoError || contentPermissionsError) {
			showCustomFlag({
				title: formatMessage(messages.mentionFlagFailedTitleWithoutDisplayName),
				description: formatMessage(messages.mentionFlagFailedDescription),
			});
		}
	}, [
		isMentionsNotificationsEnabled,
		formatMessage,
		userInfoError,
		contentPermissionsError,
		showCustomFlag,
	]);

	useEffect(() => {
		if (!isMentionsNotificationsEnabled) {
			return;
		}

		if (userInfoData?.user && !contentPermissionsLoading) {
			const { displayName, photos } = userInfoData.user;
			const userPhoto = photos[0]?.value;

			if (!isSidebarPresent) {
				if (!userHasAccessToContent) {
					showFlagForRestrictedUser({ displayName, userPhoto });
				} else {
					showFlagForUnrestrictedUser({ displayName, userPhoto });
				}
			} else if (isSidebarPresent && !userHasAccessToContent) {
				showFlagForRestrictedUser({ displayName, userPhoto });
			}
		}

		// `isSidebarPresent` is left out of dependeny array because a flag will show up anytime
		// the inline comments sidebar is dismissed / brought up after an @ mention. We want this
		// hook to run only once per mention.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		userInfoData,
		isMentionsNotificationsEnabled,
		contentPermissionsLoading,
		userHasAccessToContent,
		showFlagForRestrictedUser,
		showFlagForUnrestrictedUser,
	]);

	const handleOnMentionClickEvent = (event: UIAnalyticsEvent) => {
		const { actionSubject, action, attributes } = event.payload;
		if (actionSubject === 'mentionTypeahead' && (action === 'clicked' || action === 'pressed')) {
			if (attributes.userType === 'TEAM') {
				// TODO: support group mentions when batching is implemented
				return;
			}

			// Reset mentionedUserId for the case where the 2nd mentioned user is the same as the 1st
			resetState();

			const mentionLocalId = attributes.localId;
			setMentionLocalId(mentionLocalId);

			const accountId = attributes.userId;
			if (attributes.taskItemId) {
				setTaskItem({
					operation: Operation.ASSIGNED,
					recipientAccountId: accountId,
					// `taskId` in a draft is initially set as a uuid (e.g. 123e456-789b-12d3-a456)
					// and is converted to a numeric digit after publish (e.g. 567).
					// The BE is expecting taskId to be a long, which cannot contain characters like a hyphen
					// and thus `createInlineTaskNotificationMutation` will fail if we pass in a uuid (an invalid type).
					// To circumvent this limitation, send a dummy `taskId` of 0; this means the email notification
					// will not contain page context and will simply state that the user has been assigned a task on a page.
					taskId: '0',
				});
			}

			setMentionedUserId(accountId);
		}
	};

	const handleOnMentionPasteEvent = (event: UIAnalyticsEvent) => {
		const { actionSubject, action, attributes } = event.payload;
		if (actionSubject === 'document' && action === 'pasted') {
			resetState();

			// `attributes.mentionIds` will be an empty array when pasting any text that
			// isn't a mention, so we can safely access it here
			const mentionedUserIds = attributes.mentionIds;
			if (!mentionedUserIds.length || mentionedUserIds.length > 1) {
				// TODO: support multiple pasted mentioned IDs
				return;
			}

			const accountId = mentionedUserIds[0];
			const localId = attributes.mentionLocalIds[0];
			setMentionedUserId(accountId);
			setMentionLocalId(localId);
		}
	};

	if (!isMentionsNotificationsEnabled) {
		return <>{children}</>;
	}

	return (
		<AnalyticsListener onEvent={handleOnMentionClickEvent} channel={FabricChannel.elements}>
			<AnalyticsListener onEvent={handleOnMentionPasteEvent} channel={FabricChannel.editor}>
				{children}
			</AnalyticsListener>
		</AnalyticsListener>
	);
};

const messages = defineMessages({
	mentionFlagTitle: {
		id: 'mentions-notifications.mention.flag.title',
		defaultMessage: '{displayName} will be notified',
		description: 'Title text for the mention flag when a user is mentioned',
	},
	mentionFlagDescription: {
		id: 'mentions-notifications.mention.flag.description',
		defaultMessage: 'We’ll let them know you’ve mentioned them.',
		description: 'Body text for the mention flag when a user is mentioned',
	},
	taskMentionFlagTitle: {
		id: 'mentions-notifications.task.mention.flag.title',
		defaultMessage: 'We’ll notify {displayName}',
		description: 'Title text for the mention flag when a user is mentioned in a task',
	},
	taskMentionFlagDescription: {
		id: 'mentions-notifications.task.mention.flag.description',
		defaultMessage: 'We’ll let them know you assigned them a task just now.',
		description: 'Body text for the mention flag when a user is mentioned in a task',
	},
	mentionFlagFailedTitle: {
		id: 'mentions-notifications.mention.flag.failed.title',
		defaultMessage: 'We weren’t able to notify {displayName}',
		description: 'Title text for flag when the mention request fails',
	},
	mentionFlagFailedTitleWithoutDisplayName: {
		id: 'mentions-notifications.mention.flag.failed.generic.title',
		defaultMessage: 'We weren’t able to notify the user',
		description: 'Title text without display name for flag when the mentioned user request fails',
	},
	mentionFlagFailedDescription: {
		id: 'mentions-notifications.mention.flag.failed.description',
		defaultMessage: 'In order to notify them, you’ll need to @ mention them again.',
		description: 'Description text for flag when the mention request fails',
	},
	mentionFlagDoNotNotifyButton: {
		id: 'mentions-notifications.mention.flag.do.not.notify.button',
		defaultMessage: 'Don’t notify',
		description: 'Button in the mention flag to prevent a notification from sending a mention',
	},
	mentionFlagNoPermissionsTitle: {
		id: 'mentions-notifications.mention.flag.no.permissions.title',
		defaultMessage: '{displayName} won’t be notified',
		description:
			'Title text for mention flag for a user who does not have permissions to access the page',
	},
	taskMentionFlagNoPermissionsTitle: {
		id: 'mentions-notifications.task.mention.flag.no.permissions.title',
		defaultMessage: '{displayName} can’t be assigned a task',
		description:
			'Title text for task mention flag for a user who does not have permissions to access the page',
	},
	mentionFlagNoPermissionsDescription: {
		id: 'mentions-notifications.mention.flag.no.permissions.description',
		defaultMessage:
			'They don’t have access to this page, so we can’t let them know you mentioned them.',
		description:
			'Description text for mention flag for a user who does not have permissions to access the page',
	},
	taskMentionFlagNoPermissionsDescription: {
		id: 'mentions-notifications.task.mention.flag.no.permissions.description',
		defaultMessage:
			'They don’t have access to this page, so we can’t assign them this task, or notify them.',
		description:
			'Description text for task mention flag for a user who does not have permissions to access the page',
	},
	mentionFlagNoPermissionsParentRestrictionsDescription: {
		id: 'mentions-notifications.mention.flag.no.permissions.parent.restrictions.description',
		defaultMessage:
			'They don’t have access to a parent page, so we can’t let them know you mentioned them.',
		description:
			'Description text for mention flag for a user who does not have permissions to access the page because of parent restrictions',
	},
	taskMentionFlagNoPermissionsParentRestrictionsDescription: {
		id: 'mentions-notifications.task.mention.flag.no.permissions.parent.restrictions.description',
		defaultMessage:
			'They don’t have access to a parent page, so we can’t assign them this task, or notify them.',
		description:
			'Description text for task mention flag for a user who does not have permissions to access the page because of parent restrictions',
	},
	mentionFlagNoPermissionsSpaceRestrictionsDescription: {
		id: 'mentions-notifications.mention.flag.no.permissions.space.restrictions.description',
		defaultMessage:
			'They don’t have access to the space, so we can’t let them know you mentioned them.',
		description:
			'Description text for mention flag for a user who does not have permissions to access the page because of space restrictions',
	},
	taskMentionFlagNoPermissionsSpaceRestrictionsDescription: {
		id: 'mentions-notifications.task.mention.flag.no.permissions.space.restrictions.description',
		defaultMessage:
			'They don’t have access to the space, so we can’t assign them this task, or notify them.',
		description:
			'Description text for task mention flag for a user who does not have permissions to access the page because of space restrictions',
	},
	mentionFlagShareNowButton: {
		id: 'mentions-notifications.mention.flag.share.now.button',
		defaultMessage: 'Share now',
		description: 'Button in the mention flag to share page with mentioned user',
	},
	mentionFlagShareFailedTitle: {
		id: 'mentions-notifications.mention.flag.share.failed.title',
		defaultMessage: 'We weren’t able to share this page with {displayName}',
		description: 'Title text for mention flag after page is not shared',
	},
	mentionFlagShareFailedDescription: {
		id: 'mentions-notifications.mention.flag.share.failed.description',
		defaultMessage: 'Please try again later.',
		description: 'Description text for mention flag after page is not shared',
	},
});
